import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { BackEndService } from './backEnd.service';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ToastService } from '../util/toast.service';
import { Employee } from '../models/Employees';
import { EmployeeSService } from './modelService/employeeS.service';
import { environment } from 'src/environments/environment';
import { PushNotifications } from '@capacitor/push-notifications';
import { Platform } from '@ionic/angular';
import { FeedService } from './modelService/feed.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationTriggerService {

  private empList: Employee[] = [];
  MAX_NOTIFICATION_IMAGE_SIZE = 300 * 1024;
  constructor(
    private http: HttpClient, private toast: ToastService, private feed: FeedService,
    private auth: AuthService, private empService: EmployeeSService, private platform: Platform,
    private backEnd: BackEndService, private router: Router
  ) { }

  async createAndSendNotification(title: string, body: string, type?: string, image?: any, path?: string) {
    console.log('🚀 Triggering notification:', { title, body, type, image, path });
    const finalImage = await this.prepareNotificationImage(image, type);
    try {
      console.log('📝 Creating notification in Backend...');
      const tokenRecords = await this.backEnd.searchObject('notify', [
        ['type', 'fcm_token'],
        ['active', true]
      ]);

      if (!tokenRecords || tokenRecords.length === 0) {
        console.warn('⚠️ No FCM tokens found in database');
        return {
          success: false,
          message: 'No users to send notification'
        };
      }
      const fcmList = tokenRecords.map((t: any) => t.fcm_list).join(',');
      const notifyId = `notify_${Date.now()}`;
      const responseSave = await this.backEnd.saveObject('notify', {
        id: notifyId,
        emp_id: this.auth.loggedInEmployee?.id || null,
        business_id: this.auth.business?.id || null,
        type: type || 'general',
        title: title,
        body: body,
        image: finalImage || null,
        fcm_list: fcmList,
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

      if (!responseSave) {
        console.error('❌ Error saving notification');
        throw new Error('Failed to save notification');
      }
      console.log('✅ Notification saved to Backend:', JSON.stringify(notifyId));
      console.log('📤 Sending FCM notifications...');
      const response = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/api/vanshavali/notify/send`, {
          notificationId: notifyId,
          tokens: tokenRecords.map((t: any) => t.fcm_list),
          title: title,
          body: body,
          type: type || 'general',
          image: finalImage || null,
          path: path || '/feed'
        })
      );
      console.log('✅ Notifications sent successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Error in createAndSendNotification:', error);
      throw error;
    }
  }

  async getNotifications() {
    const data = await this.backEnd.searchObject('notify', [
      ['type', 'fcm_token', 'neq']
    ], 0, 50, 'created_at', true);
    return data;
  }
  async getNotificationById(id: string) {
    const data = await this.backEnd.getObjectById('notify', id);
    return data;
  }

  async deactivateNotification(id: string) {
    const response = await this.backEnd.saveObject('notify', {
      id: id,
      active: false,
      updated_at: new Date().toISOString()
    });
    if (!response) {
      console.error('❌ Error deactivating notification');
      throw new Error('Failed to deactivate notification');
    }
    return { success: true, message: 'Notification deactivated' };
  }

  async sendNotificationToAreaManagers(title: string, body: string, image: any, path: string, type?: string) {
    const finalImage = await this.prepareNotificationImage(image, type);

    try {
      console.log("🔍 Finding area managers locally in frontend...");
      this.empList = await this.empService.findAreaManger(this.auth.loggedInEmployee.user.user_pd);

      if (!this.empList || this.empList.length === 0) {
        this.toast.error("No manager found for notification");
        return;
      }

      const eligibleManagerIds = this.empList
        .filter(emp => emp.assigned_area?.length > 0 || ["super_admin", "admin"].includes(emp.designation))
        .map(emp => emp.id);

      if (eligibleManagerIds.length === 0) {
        console.warn("⚠️ No eligible managers found after filtering.");
        return;
      }

      console.log(`📤 Sending notifications to ${eligibleManagerIds.length} manager IDs via TSA...`);
      const response: any = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/api/vanshavali/notify/by-ids`, {
          title,
          body,
          image: finalImage || null,
          path: path || '/feed',
          type: type || 'area_manager',
          empIds: eligibleManagerIds,
          business_id: this.auth.business?.id || 'Vanshavali'
        })
      );

      if (response && response.success) {
        console.log(`✅ TSA Backend successfully notified ${response.sentCount || 0} manager(s)`);
      }
    } catch (error) {
      console.error("❌ Failed to send area manager notification via TSA:", error);
    }
  }
  initializeFCMSoundChannel() {
    PushNotifications.createChannel({
      id: 'vanshavali_notifications',
      name: 'Vanshavali Notifications',
      description: 'App Notification Channel',
      importance: 5, // **HIGH**
      sound: 'default', // ensure sound is enabled
      vibration: true,
      visibility: 1
    });
  }

  setupNotificationClickListener() {
    if (!this.platform.is('capacitor')) {
      return;
    }
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log("🔔 Notification Clicked →", notification);
      const path = notification.notification?.data?.path;
      if (path) {
        console.log("📍 Navigating to:", path);
        this.router.navigateByUrl(path);
      } else {
        console.warn("⚠ No path found in Notification Payload");
      }
    });
  }

  getDefaultImageByType(type: string) {
    const tree = 'https://firebasestorage.googleapis.com/v0/b/dev-allyot.appspot.com/o/vanshavali%2FfeedIcon%2Fvolunteer.png?alt=media'
    if (!type) {
      return tree;
    }
    const map = {
      info: 'https://firebasestorage.googleapis.com/v0/b/dev-allyot.appspot.com/o/vanshavali%2FfeedIcon%2Fwin.png?alt=media',
      condolence: 'https://firebasestorage.googleapis.com/v0/b/dev-allyot.appspot.com/o/vanshavali%2FfeedIcon%2Fcfulbti.png?alt=media',
      marriage: 'https://firebasestorage.googleapis.com/v0/b/dev-allyot.appspot.com/o/vanshavali%2FfeedIcon%2Ffeed_marriage.png?alt=media',
      event: 'https://firebasestorage.googleapis.com/v0/b/dev-allyot.appspot.com/o/vanshavali%2FfeedIcon%2Ffeed_event.png?alt=media',
      bhawan: 'https://firebasestorage.googleapis.com/v0/b/dev-allyot.appspot.com/o/vanshavali%2FfeedIcon%2Fbhawan.png?alt=media',
      business: 'https://firebasestorage.googleapis.com/v0/b/dev-allyot.appspot.com/o/vanshavali%2FfeedIcon%2Ffeed_biz.png?alt=media',
      job: 'https://firebasestorage.googleapis.com/v0/b/dev-allyot.appspot.com/o/vanshavali%2FfeedIcon%2Fnews_job.png?alt=media'
    };
    console.log("Using default image for type:", map[type]);
    return map[type] || tree;
  }

  async prepareNotificationImage(image: any, type: string) {
    if (!image) {
      return this.getDefaultImageByType(type);
    }
    const originalSize = this.feed.lastImageSize?.bytes || 0;
    if (originalSize === 0 || originalSize > this.MAX_NOTIFICATION_IMAGE_SIZE) {
      console.warn("⚠ Image too large → Using default image");
      return this.getDefaultImageByType(type);
    }
    return image;
  }

}

